import { BRANDS_CATEGORY_ID } from "@faire/help-center-utils/consts/BRANDS_CATEGORY_ID";
import { RETAILERS_CATEGORY_ID } from "@faire/help-center-utils/consts/RETAILERS_CATEGORY_ID";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import {
  loadingStateCss,
  pageMaxWidthCss,
} from "@faire/help-center/components/Mixin";
import { viewAllText } from "@faire/help-center/consts";
import { useSectionStore } from "@faire/help-center/hooks/useSectionStore";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterViewAllPopularGuidesClick } from "@faire/web-api--source/events/helpCenter/click/viewAllPopularGuides";
import popularGuidesPage from "@faire/web-api--source/routes/support/popular-guides/categoryId";
import * as React from "react";
import styled from "styled-components";

import { NAVIGATION_FROM } from "../eventEnum";

import { PopularArticlesList } from "./PopularArticlesList";
import { TermsList } from "./TermsList";

interface IProps {
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

export const PopularGuides: React.FC<IProps> = ({ loadTimingRef }) => {
  const { isLoading, retailerPopularGuides, brandPopularGuides } =
    useSectionStore();
  const { strictLocalize } = useStrictLocalization();
  const { isMobile } = useViewport();

  const handleViewAllClick = (section: NAVIGATION_FROM) => {
    trackHelpCenterViewAllPopularGuidesClick(
      section,
      getRetailerToken() ?? "",
      getBrandToken() ?? ""
    );
  };

  return (
    <Wrapper $isLoading={isLoading} ref={loadTimingRef}>
      <Column flex={1} align="flex-start">
        <Typography variant="displaySSerifRegular">
          <StrictLocalMsg defaultMessage="Popular guides for retailers" />
        </Typography>
        <Spacer height="3x" />
        <PopularArticlesList
          articles={retailerPopularGuides.articles}
          navigatedFrom={NAVIGATION_FROM.POPULAR_RETAILER_GUIDES}
        />
        <Button
          variant="tertiary"
          linkTo={popularGuidesPage(`${RETAILERS_CATEGORY_ID}`)}
          onClick={() =>
            handleViewAllClick(NAVIGATION_FROM.POPULAR_RETAILER_GUIDES)
          }
        >
          {viewAllText(
            retailerPopularGuides.number_of_articles,
            strictLocalize
          )}
        </Button>
      </Column>

      {isMobile ? <Spacer height="4x" /> : null}

      {brandPopularGuides?.articles?.length > 0 ? (
        <Column flex={1} align="flex-start">
          <Typography variant="displaySSerifRegular">
            <StrictLocalMsg defaultMessage="Popular guides for brands" />
          </Typography>
          <Spacer height="3x" />
          <PopularArticlesList
            articles={brandPopularGuides.articles}
            navigatedFrom={NAVIGATION_FROM.POPULAR_BRAND_GUIDES}
          />
          <Button
            variant="tertiary"
            linkTo={popularGuidesPage(`${BRANDS_CATEGORY_ID}`)}
            onClick={() =>
              handleViewAllClick(NAVIGATION_FROM.POPULAR_BRAND_GUIDES)
            }
          >
            {viewAllText(brandPopularGuides.number_of_articles, strictLocalize)}
          </Button>
        </Column>
      ) : null}
      {isMobile ? <Spacer height="4x" /> : null}
      <Column flex={1} align="flex-start">
        <Typography variant="displaySSerifRegular">
          <StrictLocalMsg
            defaultMessage="Policies, privacy, and security"
            description={{
              text: "Title for a section containing several articles about policies, privacy, and security",
              img: "https://cdn.faire.com/fastly/375a3a36b944ea91d46d6ccfd65eced7689974366e87ab2fbb586193e8f7b928.png",
            }}
          />
        </Typography>
        <Spacer height="3x" />
        <TermsList navigatedFrom={NAVIGATION_FROM.POLICIES_PRIVACY_SECURITY} />
      </Column>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $isLoading: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 24px 24px 32px;

  ${responsiveMediaQueries.tabletAndAbove} {
    flex-direction: row;
    padding: 40px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 56px;
  }

  ${loadingStateCss}
  ${pageMaxWidthCss}
`;
